import { useState } from 'react';
import { Link } from 'react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useForm, type UseFormReturn } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Cargando } from '@/shared/ui/Cargando';
import {
  Activity,
  AlertCircle,
  Building2,
  CheckCircle2,
  ChevronLeft,
  ChevronRight,
  Hash,
  Loader2,
  MapPin,
  Save,
  Search,
  Settings2,
  Shield,
} from 'lucide-react';
import { api } from '@/shared/api/cliente';
import { ErrorDeApi } from '@/shared/api/problema';
import type { ContribuyenteSri, Emisor } from '@/shared/api/tipos';
import { PoliticasDeCobro } from './PoliticasDeCobro';
import { PanelDeLogotipo } from './PanelDeLogotipo';
import { PasoCertificado } from './PasoCertificado';
import { Campo, Marca, Titulo, entrada } from './piezasDelAsistente';

const esquema = z.object({
  razonSocial: z.string().min(1, 'La razón social es obligatoria').max(255),
  nombreComercial: z.string().max(255).optional(),
  ruc: z.string().regex(/^[0-9]{13}$/, 'El RUC debe tener exactamente 13 dígitos'),
  direccionMatriz: z.string().min(1, 'La dirección matriz es obligatoria').max(255),
  direccionEstablecimiento: z.string().max(255).optional(),
  contribuyenteEspecial: z.string().max(50).optional(),
  agenteRetencion: z.string().max(50).optional(),
  agenteRetencionResolucion: z.string().max(100).optional(),
  contribuyenteRimpe: z.boolean(),
  obligadoContabilidad: z.boolean(),
  ambiente: z.enum(['1', '2']),
});

type Formulario = z.infer<typeof esquema>;
type Control = UseFormReturn<Formulario>;

const PASOS = [
  { titulo: 'Datos de empresa', subtitulo: 'Información del emisor', icono: Building2 },
  { titulo: 'Facturación', subtitulo: 'Configuración', icono: Settings2 },
  { titulo: 'Certificado', subtitulo: 'Firma electrónica', icono: Shield },
  { titulo: 'Estado', subtitulo: 'Verificación del sistema', icono: Activity },
] as const;

const CAMPOS_POR_PASO: Record<number, readonly (keyof Formulario)[]> = {
  0: ['razonSocial', 'nombreComercial', 'ruc', 'direccionMatriz', 'direccionEstablecimiento'],
  1: [
    'ambiente',
    'contribuyenteEspecial',
    'agenteRetencion',
    'agenteRetencionResolucion',
    'contribuyenteRimpe',
    'obligadoContabilidad',
  ],
};

export function PaginaEmpresa() {
  const emisor = useQuery({ queryKey: ['emisor'], queryFn: () => api.get<Emisor>('/emisor') });

  if (emisor.isPending) {
    return (
      <Cargando
        titulo="Cargando los datos de la empresa"
        detalle="Emisor, certificado y marca"
        alto="h-[640px]"
      />
    );
  }

  if (emisor.isError || !emisor.data) {
    return (
      <p
        role="alert"
        className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-900"
      >
        No se pudieron cargar los datos de la empresa.
      </p>
    );
  }

  return <Asistente key={emisor.data.ruc} datos={emisor.data} />;
}

function Asistente({ datos }: { datos: Emisor }) {
  const clienteDeConsultas = useQueryClient();
  const [paso, setPaso] = useState(0);
  const [guardado, setGuardado] = useState(false);
  const [errorGeneral, setErrorGeneral] = useState<string | null>(null);

  const f = useForm<Formulario>({
    resolver: zodResolver(esquema),
    defaultValues: aFormulario(datos),
  });

  const guardar = useMutation({
    mutationFn: (valores: Formulario) => api.put<Emisor>('/emisor', valores),
    onSuccess: (guardadoEnServidor) => {

      f.reset(aFormulario(guardadoEnServidor));
      setGuardado(true);
      setErrorGeneral(null);
      void clienteDeConsultas.invalidateQueries({ queryKey: ['emisor'] });
    },
    onError: (e) =>
      setErrorGeneral(e instanceof ErrorDeApi ? e.message : 'No se pudieron guardar los cambios'),
  });

  const enviar = f.handleSubmit(
    (valores) => guardar.mutate(valores),
    (errores) => {

      const primero = [0, 1].find((p) => CAMPOS_POR_PASO[p]?.some((c) => c in errores));
      if (primero !== undefined) {
        setPaso(primero);
      }
    },
  );

  const { isDirty, isSubmitting } = f.formState;
  const ultimo = paso === PASOS.length - 1;

  return (
    <form onSubmit={enviar} noValidate>
      <div className="overflow-hidden rounded-xl border border-armazon-200 bg-white shadow-sm">
        <PasosDelAsistente paso={paso} alElegir={setPaso} />

        <div className="min-h-[480px] p-6 lg:p-8">
          {errorGeneral && (
            <p
              role="alert"
              className="mb-5 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-900"
            >
              {errorGeneral}
            </p>
          )}

          {guardado && !isDirty && (
            <p className="mb-5 flex items-center gap-2 rounded-lg border border-marca-300 bg-marca-50 px-4 py-3 text-sm text-marca-900">
              <CheckCircle2 className="size-4 shrink-0" aria-hidden />
              Los cambios se guardaron.
            </p>
          )}

          {paso === 0 && <PasoEmpresa f={f} tieneLogo={datos.tieneLogo} />}
          {paso === 1 && <PasoFacturacion f={f} />}
          {paso === 2 && <PasoCertificado datos={datos} />}
          {paso === 3 && <PasoEstado f={f} datos={datos} />}
        </div>

        <div className="flex items-center justify-between gap-3 border-t border-armazon-100 bg-armazon-50/60 px-6 py-4 lg:px-8">
          <button
            type="button"
            onClick={() => setPaso(Math.max(0, paso - 1))}
            disabled={paso === 0}
            className="inline-flex items-center gap-2 rounded-xl px-4 py-2.5 text-sm font-medium text-armazon-700 transition-colors enabled:border enabled:border-armazon-200 enabled:bg-white enabled:shadow-sm enabled:hover:bg-armazon-50 disabled:cursor-not-allowed disabled:text-armazon-400"
          >
            <ChevronLeft className="size-4" aria-hidden /> Anterior
          </button>

          <div className="flex items-center gap-2">

            {isDirty && !ultimo && (
              <button
                type="submit"
                disabled={isSubmitting}
                className="inline-flex items-center gap-2 rounded-xl border border-marca-300 bg-white px-4 py-2.5 text-sm font-semibold text-marca-700 transition-colors hover:bg-marca-50 disabled:opacity-50"
              >
                <Save className="size-4" aria-hidden /> Guardar
              </button>
            )}

            {ultimo ? (
              <button
                type="submit"
                disabled={isSubmitting || !isDirty}
                className="inline-flex items-center gap-2 rounded-xl bg-marca-600 px-6 py-2.5 text-sm font-semibold text-white shadow-sm transition-colors hover:bg-marca-700 disabled:cursor-not-allowed disabled:opacity-50"
              >
                <Save className="size-4" aria-hidden />
                {isSubmitting ? 'Guardando…' : 'Guardar'}
              </button>
            ) : (
              <button
                type="button"
                onClick={() => setPaso(paso + 1)}
                className="inline-flex items-center gap-2 rounded-xl bg-marca-600 px-5 py-2.5 text-sm font-semibold text-white shadow-sm transition-colors hover:bg-marca-700"
              >
                Siguiente <ChevronRight className="size-4" aria-hidden />
              </button>
            )}
          </div>
        </div>
      </div>
    </form>
  );
}

function PasosDelAsistente({ paso, alElegir }: { paso: number; alElegir: (n: number) => void }) {
  return (
    <div className="border-b border-armazon-100 bg-armazon-50/60 px-4">
      <nav aria-label="Pasos de la configuración" className="-mb-px flex gap-1 overflow-x-auto">
        {PASOS.map((p, i) => {
          const activo = paso === i;
          const recorrido = paso > i;
          const Icono = p.icono;
          return (
            <button
              key={p.titulo}
              type="button"
              aria-current={activo ? 'step' : undefined}
              onClick={() => alElegir(i)}
              className={[
                'flex shrink-0 items-center gap-2.5 border-b-2 px-5 py-3.5 text-sm font-medium transition-colors',
                activo
                  ? 'rounded-t-lg border-marca-500 bg-white text-marca-700'
                  : recorrido
                    ? 'border-transparent text-marca-600 hover:text-marca-700'
                    : 'border-transparent text-armazon-500 hover:text-armazon-700',
              ].join(' ')}
            >
              <span
                className={[
                  'grid size-7 shrink-0 place-items-center rounded-lg',
                  activo
                    ? 'bg-marca-100 text-marca-700'
                    : recorrido
                      ? 'bg-marca-50 text-marca-600'
                      : 'bg-armazon-100 text-armazon-500',
                ].join(' ')}
              >
                {recorrido ? (
                  <CheckCircle2 className="size-3.5" aria-hidden />
                ) : (
                  <Icono className="size-3.5" aria-hidden />
                )}
              </span>
              <span className="hidden text-left sm:block">
                <span className="block leading-tight">{p.titulo}</span>
                <span
                  className={`block text-[10px] font-normal uppercase leading-tight ${
                    activo ? 'text-marca-600' : 'text-armazon-500'
                  }`}
                >
                  {p.subtitulo}
                </span>
              </span>
            </button>
          );
        })}
      </nav>
    </div>
  );
}

function PasoEmpresa({ f, tieneLogo }: { f: Control; tieneLogo: boolean }) {
  const [avisoSri, setAvisoSri] = useState<string | null>(null);

  const consulta = useMutation({
    mutationFn: (identificacion: string) =>
      api.get<ContribuyenteSri>(`/emisor/consulta/${identificacion}`),
    onSuccess: (c) => {

      const traer = (
        campo:
          | 'razonSocial'
          | 'nombreComercial'
          | 'direccionMatriz'
          | 'direccionEstablecimiento',
        valor: string | null,
      ) => {
        if (valor) {
          f.setValue(campo, valor, { shouldDirty: true });
        }
      };
      traer('razonSocial', c.razonSocial);
      traer('nombreComercial', c.nombreComercial);
      traer('direccionMatriz', c.direccionMatriz);

      traer('direccionEstablecimiento', c.direccionEstablecimiento);

      if (c.obligadoContabilidad != null) {
        f.setValue('obligadoContabilidad', c.obligadoContabilidad, { shouldDirty: true });
      }

      setAvisoSri(
        `Datos traídos ${c.fuente === 'convenio' ? 'del convenio de identidad' : 'del SRI'}${
          c.estado ? ` · ${c.estado}` : ''
        }${c.correo ? ` · correo registrado: ${c.correo}` : ''}. Revíselos antes de guardar.`,
      );
    },
    onError: (e) =>
      setAvisoSri(e instanceof ErrorDeApi ? e.message : 'No se pudo consultar el SRI'),
  });

  const ruc = f.watch('ruc') ?? '';
  const consultable = /^[0-9]{10}([0-9]{3})?$/.test(ruc.trim());

  return (
    <div className="grid gap-8 lg:grid-cols-3">
      <div className="lg:col-span-1">
        <PanelDeLogotipo tieneLogo={tieneLogo} />
      </div>

      <div className="grid content-start gap-4 sm:grid-cols-2 lg:col-span-2">
        <Campo
          etiqueta="Razón social"
          requerido
          error={f.formState.errors.razonSocial?.message}
          ancho="completo"
        >
          <div className="relative">
            <Building2
              className="pointer-events-none absolute left-3.5 top-1/2 size-4 -translate-y-1/2 text-armazon-400"
              aria-hidden
            />
            <input {...f.register('razonSocial')} className={`${entrada} pl-10`} />
          </div>
        </Campo>

        <Campo etiqueta="Nombre comercial" error={f.formState.errors.nombreComercial?.message}>
          <input {...f.register('nombreComercial')} className={entrada} />
        </Campo>

        <Campo
          etiqueta="RUC"
          requerido
          error={f.formState.errors.ruc?.message}
          ayuda="13 dígitos · la lupa trae los datos"
        >
          <div className="flex gap-2">
            <div className="relative flex-1">
              <Hash
                className="pointer-events-none absolute left-3.5 top-1/2 size-4 -translate-y-1/2 text-armazon-400"
                aria-hidden
              />
              <input
                {...f.register('ruc')}
                inputMode="numeric"
                maxLength={13}
                className={`${entrada} importe pl-10`}
              />
            </div>
            <button
              type="button"
              onClick={() => consulta.mutate(ruc.trim())}
              disabled={consulta.isPending || !consultable}
              title="Traer la razón social y la dirección de esta identificación"
              aria-label="Traer los datos de esta identificación"
              className="inline-flex shrink-0 items-center gap-1.5 rounded-lg bg-indigo-600 px-3 text-xs font-semibold text-white transition-colors hover:bg-indigo-700 disabled:cursor-not-allowed disabled:opacity-50"
            >
              {consulta.isPending ? (
                <Loader2 className="size-3.5 animate-spin" aria-hidden />
              ) : (
                <Search className="size-3.5" aria-hidden />
              )}
            </button>
          </div>
        </Campo>

        {avisoSri && (
          <p
            role="status"

            className={`text-sm uppercase sm:col-span-2 ${
              consulta.isError ? 'text-alerta' : 'text-marca-700'
            }`}
          >
            {avisoSri}
          </p>
        )}

        <Campo
          etiqueta="Dirección matriz"
          requerido
          error={f.formState.errors.direccionMatriz?.message}
          ancho="completo"
        >
          <div className="relative">
            <MapPin
              className="pointer-events-none absolute left-3.5 top-1/2 size-4 -translate-y-1/2 text-armazon-400"
              aria-hidden
            />
            <input {...f.register('direccionMatriz')} className={`${entrada} pl-10`} />
          </div>
        </Campo>

        <Campo
          etiqueta="Dirección del establecimiento"
          error={f.formState.errors.direccionEstablecimiento?.message}
          ayuda="Si es distinta de la matriz"
          ancho="completo"
        >
          <input {...f.register('direccionEstablecimiento')} className={entrada} />
        </Campo>
      </div>
    </div>
  );
}

function PasoFacturacion({ f }: { f: Control }) {
  const { errors } = f.formState;
  const ambiente = f.watch('ambiente');
  const esAgente = Boolean(f.watch('agenteRetencion')?.trim());

  return (
    <div className="grid gap-6">
      <div className="grid gap-6 lg:grid-cols-2">
        <div className="grid content-start gap-4">
          <Titulo icono={Hash} color="marca" texto="Datos de facturación" />

          <p className="rounded-xl border border-blue-200 bg-blue-50 p-4 text-xs uppercase text-blue-800">
            <span className="mb-1 block font-semibold uppercase">
              Establecimiento, punto de emisión y secuencial
            </span>
            Se configuran en el módulo de{' '}
            <Link to="/puntos-emision" className="font-semibold underline">
              Puntos de emisión
            </Link>
            . Cada caja tiene su propio punto y su secuencial independiente.
          </p>

          <p className="rounded-xl border border-marca-300 bg-marca-50 p-4 text-xs uppercase text-marca-800">
            <span className="mb-1 block font-semibold uppercase">Emisión y envío al SRI</span>
            La programación de la emisión automática y el envío por lotes se configuran en{' '}
            <Link to="/configuracion/emision" className="font-semibold underline">
              Configuración → Emisión
            </Link>
            .
          </p>

          <PoliticasDeCobro />
        </div>

        <div className="grid content-start gap-4">
          <Titulo icono={Settings2} color="azul" texto="Ambiente del SRI" />
          <p className="-mt-2 text-xs uppercase tracking-wide text-armazon-600">
            Es la diferencia entre ensayar y emitir de verdad
          </p>

          <div className="grid gap-3 sm:grid-cols-2">
            {(
              [
                { valor: '1', etiqueta: 'Pruebas', nota: 'Desarrollo y ensayo' },
                { valor: '2', etiqueta: 'Producción', nota: 'Ambiente real del SRI' },
              ] as const
            ).map((opcion) => {
              const elegido = ambiente === opcion.valor;
              const ensayo = opcion.valor === '1';
              return (
                <button
                  key={opcion.valor}
                  type="button"
                  onClick={() => f.setValue('ambiente', opcion.valor, { shouldDirty: true })}
                  aria-pressed={elegido}
                  className={[
                    'rounded-xl border-2 p-4 text-left transition-colors',
                    elegido
                      ? ensayo
                        ? 'border-amber-400 bg-amber-50'
                        : 'border-marca-500 bg-marca-50'
                      : 'border-armazon-200 hover:border-armazon-300 hover:bg-armazon-50',
                  ].join(' ')}
                >
                  <span className="flex items-center gap-3">
                    <span
                      className={[
                        'grid size-4 shrink-0 place-items-center rounded-full border-2',
                        elegido
                          ? ensayo
                            ? 'border-amber-500'
                            : 'border-marca-600'
                          : 'border-armazon-300',
                      ].join(' ')}
                    >
                      {elegido && (
                        <span
                          className={`size-2 rounded-full ${ensayo ? 'bg-amber-500' : 'bg-marca-600'}`}
                        />
                      )}
                    </span>
                    <span className="min-w-0">
                      <span
                        className={`block text-sm font-semibold ${
                          elegido
                            ? ensayo
                              ? 'text-amber-800'
                              : 'text-marca-800'
                            : 'text-armazon-800'
                        }`}
                      >
                        {opcion.etiqueta}
                      </span>
                      <span className="mt-0.5 block text-xs uppercase text-armazon-600">
                        {opcion.nota}
                      </span>
                    </span>
                  </span>
                </button>
              );
            })}
          </div>
        </div>
      </div>

      <div className="grid gap-4">
        <Titulo icono={Shield} color="violeta" texto="Régimen tributario" />
        <p className="-mt-2 text-xs uppercase tracking-wide text-armazon-600">
          Cada marca cambia lo que se imprime en el comprobante
        </p>

        <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
          <Marca
            etiqueta="Obligado a llevar contabilidad"
            nota="Según resolución del SRI"
            activo={f.watch('obligadoContabilidad')}
            alCambiar={() =>
              f.setValue('obligadoContabilidad', !f.getValues('obligadoContabilidad'), {
                shouldDirty: true,
              })
            }
          />
          <Marca
            etiqueta="Contribuyente RIMPE"
            nota="Régimen simplificado"
            activo={f.watch('contribuyenteRimpe')}
            alCambiar={() =>
              f.setValue('contribuyenteRimpe', !f.getValues('contribuyenteRimpe'), {
                shouldDirty: true,
              })
            }
          />

          <div className="rounded-xl border border-armazon-200 bg-armazon-50/60 p-4">
            <Campo etiqueta="Contribuyente especial" error={errors.contribuyenteEspecial?.message}>
              <input
                {...f.register('contribuyenteEspecial')}
                placeholder="N.º de resolución"
                className={entrada}
              />
            </Campo>
          </div>

          <div className="rounded-xl border border-armazon-200 bg-armazon-50/60 p-4">
            <Campo etiqueta="Agente de retención" error={errors.agenteRetencion?.message}>
              <input
                {...f.register('agenteRetencion')}
                placeholder="Código del SRI"
                className={entrada}
              />
            </Campo>
          </div>

          <div className="rounded-xl border border-armazon-200 bg-armazon-50/60 p-4 sm:col-span-2 xl:col-span-4">
            <Campo
              etiqueta="Resolución del agente de retención (RIDE)"
              requerido={esAgente}
              error={errors.agenteRetencionResolucion?.message}
              ayuda={
                esAgente
                  ? 'Obligatoria al declararse agente de retención'
                  : 'Solo si es agente de retención'
              }
            >
              <input
                {...f.register('agenteRetencionResolucion')}
                placeholder="NAC-DGERCGC00-00000000"
                className={entrada}
              />
            </Campo>
          </div>
        </div>
      </div>
    </div>
  );
}

function PasoEstado({ f, datos }: { f: Control; datos: Emisor }) {
  const v = f.watch();

  const puntos = [
    { etiqueta: 'Razón social', valor: v.razonSocial, listo: Boolean(v.razonSocial?.trim()) },
    { etiqueta: 'RUC', valor: v.ruc, listo: /^[0-9]{13}$/.test(v.ruc ?? '') },
    {
      etiqueta: 'Dirección matriz',
      valor: v.direccionMatriz,
      listo: Boolean(v.direccionMatriz?.trim()),
    },
    {
      etiqueta: 'Ambiente del SRI',
      valor: v.ambiente === '2' ? 'Producción' : 'Pruebas',
      listo: v.ambiente === '2',
    },
    {
      etiqueta: 'Certificado de firma',
      valor: datos.certificado
        ? datos.certificado.vigente
          ? `Vigente · ${datos.certificado.diasRestantes} días`
          : `Venció el ${datos.certificado.validoHasta}`
        : 'Sin cargar',
      listo: datos.certificado?.vigente ?? false,
    },
  ];

  const todo = puntos.every((p) => p.listo);

  return (
    <div className="grid gap-5">
      <p className="text-sm uppercase tracking-wide text-armazon-600">
        Lo que falta para poder emitir comprobantes válidos ante el SRI
      </p>

      <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-3">
        {puntos.map((p) => (
          <div
            key={p.etiqueta}
            className={[
              'flex items-center gap-3 rounded-xl border p-4',
              p.listo ? 'border-marca-200 bg-marca-50/60' : 'border-amber-200 bg-amber-50/60',
            ].join(' ')}
          >
            <span
              className={[
                'grid size-8 shrink-0 place-items-center rounded-lg',
                p.listo ? 'bg-marca-100 text-marca-700' : 'bg-amber-100 text-amber-700',
              ].join(' ')}
            >
              {p.listo ? (
                <CheckCircle2 className="size-4" aria-hidden />
              ) : (
                <AlertCircle className="size-4" aria-hidden />
              )}
            </span>
            <span className="min-w-0">
              <span className="block text-[11px] font-medium uppercase tracking-wider text-armazon-600">
                {p.etiqueta}
              </span>
              <span
                className={`block truncate text-sm font-semibold ${
                  p.listo ? 'text-armazon-800' : 'text-amber-800'
                }`}
                title={p.valor ?? ''}
              >
                {p.valor || 'Sin configurar'}
              </span>
            </span>
          </div>
        ))}
      </div>

      <div
        className={[
          'flex items-center gap-4 rounded-xl border-2 p-5',
          todo ? 'border-marca-300 bg-marca-50' : 'border-amber-300 bg-amber-50',
        ].join(' ')}
      >
        <span
          className={[
            'grid size-12 shrink-0 place-items-center rounded-2xl',
            todo ? 'bg-marca-100 text-marca-700' : 'bg-amber-100 text-amber-700',
          ].join(' ')}
        >
          {todo ? (
            <CheckCircle2 className="size-6" aria-hidden />
          ) : (
            <AlertCircle className="size-6" aria-hidden />
          )}
        </span>
        <div>
          <p
            className={`text-sm font-bold uppercase ${todo ? 'text-marca-900' : 'text-amber-900'}`}
          >
            {todo ? 'Listo para facturar' : 'Configuración incompleta'}
          </p>
          <p className={`mt-0.5 text-xs uppercase ${todo ? 'text-marca-700' : 'text-amber-800'}`}>
            {todo
              ? 'Todos los datos están configurados.'
              : 'Revise los puntos marcados en ámbar antes de emitir.'}
          </p>
        </div>
      </div>
    </div>
  );
}

function aFormulario(datos: Emisor): Formulario {
  return {
    razonSocial: datos.razonSocial,
    nombreComercial: datos.nombreComercial ?? '',
    ruc: datos.ruc,
    direccionMatriz: datos.direccionMatriz,
    direccionEstablecimiento: datos.direccionEstablecimiento ?? '',
    contribuyenteEspecial: datos.contribuyenteEspecial ?? '',
    agenteRetencion: datos.agenteRetencion ?? '',
    agenteRetencionResolucion: datos.agenteRetencionResolucion ?? '',
    contribuyenteRimpe: datos.contribuyenteRimpe,
    obligadoContabilidad: datos.obligadoContabilidad,
    ambiente: datos.ambiente === '2' ? '2' : '1',
  };
}
